<script lang="ts">
	const defaultLabels = {
		play: 'Play',
		pause: 'Pause',
		mute: 'Mute',
		unmute: 'Unmute',
		volume: 'Volume',
		showTranscript: 'Show transcript',
		hideTranscript: 'Hide transcript',
		transcript: 'Transcript',
		seekAnnouncement: (text: string) => `Jumped to: ${text}`,
		playingAnnouncement: 'Audio playing',
		pausedAnnouncement: 'Audio paused',
		mutedAnnouncement: 'Audio muted',
		unmutedAnnouncement: 'Audio unmuted',
	};

	interface Props {
		src?: string;
		transcript?: Array<{ time: number; text: string }> | string;
		'vtt-url'?: string;
		'module-id'?: string;
		'module-guid'?: string;
		'page-index'?: string;
		labels?: Partial<typeof defaultLabels>;
	}

	let {
		src = '',
		transcript = '',
		'vtt-url': vttUrl = '',
		'module-id': moduleId = '',
		'module-guid': moduleGuid = '', // not used at the moment. May remove... still a WIP
		'page-index': pageIndex = '',
		labels = {},
	}: Props = $props();

	// State using Svelte 5 runes
	let audio = $state<HTMLAudioElement | null>(null);
	let audioPlayerElement = $state<HTMLElement | null>(null);

	// Try to find the host custom element by traversing up the DOM
	let hostCustomElement = $state<HTMLElement | null>(null);

	$effect(() => {
		if (audioPlayerElement) {
			// First, try looking at the shadow host (most reliable for custom elements)
			if (audioPlayerElement.getRootNode) {
				const root = audioPlayerElement.getRootNode();
				if (root && 'host' in root) {
					const shadowHost = (root as ShadowRoot).host as HTMLElement;
					if (
						shadowHost &&
						shadowHost.tagName &&
						shadowHost.tagName.toLowerCase() === 'audio-player'
					) {
						hostCustomElement = shadowHost;
						return;
					}
				}
			}

			// Fallback: Look for the audio-player custom element in the DOM tree
			let current: HTMLElement | null = audioPlayerElement;
			while (current) {
				if (current.tagName && current.tagName.toLowerCase() === 'audio-player') {
					hostCustomElement = current;
					break;
				}
				current = current.parentElement;
			}
		}
	});

	// Derived values that fallback to reading attributes if props are empty
	const effectiveModuleId = $derived(() => {
		if (moduleId) {
			return moduleId;
		}
		// Try to get from the custom element host
		if (hostCustomElement) {
			const moduleIdAttr = hostCustomElement.getAttribute('module-id');
			const idAttr = hostCustomElement.getAttribute('id');
			const result = moduleIdAttr || idAttr || '';
			return result;
		}
		return '';
	});

	const effectivePageIndex = $derived(() => {
		if (pageIndex) {
			return pageIndex;
		}
		// Try to get from the custom element host
		if (hostCustomElement) {
			return hostCustomElement.getAttribute('page-index') || '';
		}
		return '';
	});

	let isPlaying = $state(false);
	let isMuted = $state(false);
	let volume = $state(1);
	let showTranscript = $state(false);
	let currentTime = $state(0);
	let currentStep = $state(0);
	let transcriptContainer = $state<HTMLDivElement>();
	let liveMsg = $state('');
	let fetchedTranscript = $state<Array<{ time: number; text: string }>>([]);
	let showControls = $state(false); // Controls visibility

	// Derived values using Svelte 5 runes
	const mergedLabels = $derived({ ...defaultLabels, ...labels });

	// VTT parser function
	function parseVTT(vttText: string): Array<{ time: number; text: string }> {
		const lines = vttText.split('\n');
		const cues: Array<{ time: number; text: string }> = [];

		let i = 0;
		// Skip header lines until we find the first timestamp
		while (i < lines.length && !lines[i].includes('-->')) {
			i++;
		}

		while (i < lines.length) {
			const line = lines[i].trim();

			// Look for timestamp line (format: 00:00:01.772 --> 00:00:04.920)
			if (line.includes('-->')) {
				const [startTime] = line.split(' --> ');
				const timeInSeconds = parseVTTTime(startTime);

				// Collect text lines until we hit an empty line or another timestamp
				i++;
				const textLines: string[] = [];
				while (i < lines.length && lines[i].trim() !== '' && !lines[i].includes('-->')) {
					textLines.push(lines[i].trim());
					i++;
				}

				if (textLines.length > 0) {
					cues.push({
						time: timeInSeconds,
						text: textLines.join(' ').trim(),
					});
				}
			} else {
				i++;
			}
		}

		return cues;
	}

	// Convert VTT timestamp (HH:MM:SS.mmm) to seconds
	function parseVTTTime(timeString: string): number {
		const parts = timeString.split(':');
		const hours = parseInt(parts[0]);
		const minutes = parseInt(parts[1]);
		const seconds = parseFloat(parts[2]);
		return hours * 3600 + minutes * 60 + seconds;
	}

	// Effect to fetch transcript from URL
	$effect(() => {
		if (vttUrl && vttUrl.trim()) {
			fetch(vttUrl)
				.then((response) => {
					if (!response.ok) {
						throw new Error(`HTTP error! status: ${response.status}`);
					}
					return response.text(); // VTT is text/plain
				})
				.then((data) => {
					fetchedTranscript = parseVTT(data);
				})
				.catch((error) => {
					fetchedTranscript = [];
				});
		} else {
			fetchedTranscript = [];
		}
	});

	// Use provided transcript or fallback
	const parsedTranscript = $derived(
		(() => {
			// Priority: fetched transcript > direct transcript prop
			if (fetchedTranscript.length > 0) {
				return fetchedTranscript;
			}

			if (Array.isArray(transcript)) return transcript;
			if (typeof transcript === 'string' && transcript.trim()) {
				try {
					// Clean up the JSON string - remove line breaks and extra spaces but preserve JSON structure
					const cleanedTranscript = transcript
						.trim()
						.replace(/\n\s*/g, '') // Remove line breaks and following whitespace
						.replace(/\s+/g, ' ') // Replace multiple spaces with single space
						.replace(/\s*([{}[\],:])\s*/g, '$1'); // Remove spaces around JSON punctuation
					return JSON.parse(cleanedTranscript);
				} catch (error) {
					return [];
				}
			}
			return [];
		})()
	);

	// Only show transcript functionality if we have a valid transcript
	const hasTranscript = $derived(parsedTranscript.length > 0);
	function togglePlay() {
		if (audio) {
			if (audio.paused) {
				audio.play();
			} else {
				audio.pause();
			}
		}
	}

	function toggleMute() {
		if (audio) {
			audio.muted = !audio.muted;
		}
	}

	function onVolumeInput(e: Event) {
		const v = +(e.target as HTMLInputElement).value;
		if (audio) {
			audio.volume = v;
		}
	}

	function toggleTranscript() {
		showTranscript = !showTranscript;
		if (showTranscript && transcriptContainer) {
			transcriptContainer.focus();
		}
	}

	function seekTo(time: number, idx: number) {
		if (audio) {
			audio.currentTime = time;
			currentStep = idx;
			announce(mergedLabels.seekAnnouncement(parsedTranscript[idx].text));
		}
	}

	// Accessibility live region
	function announce(msg: string) {
		liveMsg = '';
		setTimeout(() => (liveMsg = msg), 10);
	}

	// Keyboard navigation for transcript
	function onTranscriptKeydown(e: KeyboardEvent, idx: number) {
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			const next = Math.min(idx + 1, parsedTranscript.length - 1);
			seekTo(parsedTranscript[next].time, next);
			document.getElementById(`transcript-step-${next}`)?.focus();
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			const prev = Math.max(idx - 1, 0);
			seekTo(parsedTranscript[prev].time, prev);
			document.getElementById(`transcript-step-${prev}`)?.focus();
		} else if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			seekTo(parsedTranscript[idx].time, idx);
		}
	}

	// Effects using Svelte 5 runes
	$effect(() => {
		if (audio) {
			const handlePlay = (e: Event) => {
				isPlaying = true;
				announce(mergedLabels.playingAnnouncement);
				// Only dispatch for trusted events
				if (e.isTrusted && audio) {
					const customPlayEvent = new Event('play', { bubbles: true, composed: true });
					Object.defineProperty(customPlayEvent, 'target', { value: e.target, writable: false });
					audio.dispatchEvent(customPlayEvent);
				}
			};
			const handlePause = (e: Event) => {
				isPlaying = false;
				announce(mergedLabels.pausedAnnouncement);
				if (e.isTrusted && audio) {
					const customPauseEvent = new Event('pause', { bubbles: true, composed: true });
					Object.defineProperty(customPauseEvent, 'target', { value: e.target, writable: false });
					audio.dispatchEvent(customPauseEvent);
				}
			};
			const handleVolumeChange = () => {
				if (audio) {
					isMuted = audio.muted;
					volume = audio.volume;
					announce(audio.muted ? mergedLabels.mutedAnnouncement : mergedLabels.unmutedAnnouncement);
				}
			};
			const handleTimeUpdate = (e: Event) => {
				if (audio) {
					currentTime = audio.currentTime;
					let idx = 0;
					for (let i = 0; i < parsedTranscript.length; i++) {
						if (audio.currentTime >= parsedTranscript[i].time) {
							idx = i;
						}
					}
					currentStep = idx;
					if (e.isTrusted && audio) {
						const customTimeUpdateEvent = new Event('timeupdate', { bubbles: true, composed: true });
						Object.defineProperty(customTimeUpdateEvent, 'target', { value: e.target, writable: false });
						audio.dispatchEvent(customTimeUpdateEvent);
					}
				}
			};
			const handleEnded = (e: Event) => {
				isPlaying = false;
				if (e.isTrusted && audio) {
					const customEndedEvent = new Event('ended', { bubbles: true, composed: true });
					Object.defineProperty(customEndedEvent, 'target', { value: e.target, writable: false });
					audio.dispatchEvent(customEndedEvent);
				}
			};
			const handleLoadedMetadata = (e: Event) => {
				if (e.isTrusted && audio) {
					const customLoadedMetadataEvent = new Event('loadedmetadata', { bubbles: true, composed: true });
					Object.defineProperty(customLoadedMetadataEvent, 'target', { value: e.target, writable: false });
					audio.dispatchEvent(customLoadedMetadataEvent);
				}
			};
			const handleSeeking = (e: Event) => {
				if (e.isTrusted && audio) {
					const customSeekingEvent = new Event('seeking', { bubbles: true, composed: true });
					Object.defineProperty(customSeekingEvent, 'target', { value: e.target, writable: false });
					audio.dispatchEvent(customSeekingEvent);
				}
			};

			audio.addEventListener('play', handlePlay);
			audio.addEventListener('pause', handlePause);
			audio.addEventListener('volumechange', handleVolumeChange);
			audio.addEventListener('timeupdate', handleTimeUpdate);
			audio.addEventListener('ended', handleEnded);
			audio.addEventListener('loadedmetadata', handleLoadedMetadata);
			audio.addEventListener('seeking', handleSeeking);

			return () => {
				audio?.removeEventListener('play', handlePlay);
				audio?.removeEventListener('pause', handlePause);
				audio?.removeEventListener('volumechange', handleVolumeChange);
				audio?.removeEventListener('timeupdate', handleTimeUpdate);
				audio?.removeEventListener('ended', handleEnded);
				audio?.removeEventListener('loadedmetadata', handleLoadedMetadata);
				audio?.removeEventListener('seeking', handleSeeking);
			};
		}
	});
</script>

<div class="audio-player" role="region" aria-label="Audio player" bind:this={audioPlayerElement}>
	<div class="audio-player-controls">
		<audio
			bind:this={audio}
			src={src || ''}
			data-video-id={effectiveModuleId()}
			data-page-index={effectivePageIndex()}
			tabindex="-1"
			aria-hidden="true"></audio>

		<button
			type="button"
			aria-pressed={isPlaying}
			aria-label={isPlaying ? mergedLabels.pause : mergedLabels.play}
			aria-disabled={false}
			onclick={togglePlay}
			onkeydown={(e) => (e.key === ' ' || e.key === 'Enter') && (e.preventDefault(), togglePlay())}>
			{#if isPlaying}
				<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="currentColor" class="icon icon-tabler icons-tabler-filled icon-tabler-player-pause"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M9 4h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h2a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2z" /><path d="M17 4h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h2a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2z" /></svg>
			{:else}
				<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="currentColor" class="icon icon-tabler icons-tabler-filled icon-tabler-player-play"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M6 4v16a1 1 0 0 0 1.524 .852l13 -8a1 1 0 0 0 0 -1.704l-13 -8a1 1 0 0 0 -1.524 .852z" /></svg>
			{/if}
		</button>

		{#if showControls}
			<button
				type="button"
				aria-pressed={isMuted}
				aria-label={isMuted ? mergedLabels.unmute : mergedLabels.mute}
				aria-disabled={false}
				onclick={toggleMute}
				onkeydown={(e) => (e.key === ' ' || e.key === 'Enter') && (e.preventDefault(), toggleMute())}>
				{#if isMuted}
					<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-volume-off"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M15 8a5 5 0 0 1 1.912 4.934m-1.377 2.602a5 5 0 0 1 -.535 .464" /><path d="M17.7 5a9 9 0 0 1 2.362 11.086m-1.676 2.299a9 9 0 0 1 -.686 .615" /><path d="M9.069 5.054l.431 -.554a.8 .8 0 0 1 1.5 .5v2m0 4v8a.8 .8 0 0 1 -1.5 .5l-3.5 -4.5h-2a1 1 0 0 1 -1 -1v-4a1 1 0 0 1 1 -1h2l1.294 -1.664" /><path d="M3 3l18 18" /></svg>
				{:else}
					<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-volume"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M15 8a5 5 0 0 1 0 8" /><path d="M17.7 5a9 9 0 0 1 0 14" /><path d="M6 15h-2a1 1 0 0 1 -1 -1v-4a1 1 0 0 1 1 -1h2l3.5 -4.5a.8 .8 0 0 1 1.5 .5v14a.8 .8 0 0 1 -1.5 .5l-3.5 -4.5" /></svg>
				{/if}
			</button>

			<label>
				<span class="sr-only">{mergedLabels.volume}</span>
				<input type="range" min="0" max="1" step="0.01" value={volume} aria-valuenow={volume} aria-valuemin="0" aria-valuemax="1" aria-label={mergedLabels.volume} oninput={onVolumeInput} aria-disabled={false} />
			</label>
		{/if}

		{#if hasTranscript}
			<button
				type="button"
				aria-pressed={showTranscript}
				aria-label={showTranscript ? mergedLabels.hideTranscript : mergedLabels.showTranscript}
				aria-controls="transcript-panel"
				aria-expanded={showTranscript}
				aria-disabled={false}
				onclick={toggleTranscript}
				onkeydown={(e) => (e.key === ' ' || e.key === 'Enter') && (e.preventDefault(), toggleTranscript())}>
				{#if showTranscript}
					<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-file-off"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M3 3l18 18" /><path d="M7 3h7l5 5v7m0 4a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2v-14" /></svg>
				{:else}
					<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="currentColor" class="icon icon-tabler icons-tabler-filled icon-tabler-file-description"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M12 2l.117 .007a1 1 0 0 1 .876 .876l.007 .117v4l.005 .15a2 2 0 0 0 1.838 1.844l.157 .006h4l.117 .007a1 1 0 0 1 .876 .876l.007 .117v9a3 3 0 0 1 -2.824 2.995l-.176 .005h-10a3 3 0 0 1 -2.995 -2.824l-.005 -.176v-14a3 3 0 0 1 2.824 -2.995l.176 -.005zm3 14h-6a1 1 0 0 0 0 2h6a1 1 0 0 0 0 -2m0 -4h-6a1 1 0 0 0 0 2h6a1 1 0 0 0 0 -2" /><path d="M19 7h-4l-.001 -4.001z" /></svg>
				{/if}
			</button>
		{/if}
	</div>

	{#if hasTranscript}
		<div id="transcript-panel" class="transcript" role="region" aria-label={mergedLabels.transcript} aria-live="polite" tabindex="-1" hidden={!showTranscript} bind:this={transcriptContainer}>
			<ul>
				{#each parsedTranscript as step, idx}
					<li>
						<button id={`transcript-step-${idx}`} type="button" aria-current={currentStep === idx ? 'step' : undefined} aria-label={step.text} aria-disabled={false} class:active={currentStep === idx} onclick={() => seekTo(step.time, idx)} onkeydown={(e) => onTranscriptKeydown(e, idx)} tabindex="0">
							{step.text}
						</button>
					</li>
				{/each}
			</ul>
		</div>
	{/if}

	<div aria-live="polite" class="sr-only">{liveMsg}</div>
</div>

<style>
	.audio-player,
	.audio-player-controls,
	.audio-player button {
		width: 100%;
		height: 100%;
	}
	.audio-player button[aria-pressed='true'] {
		background: #e0e0e0;
	}
	.sr-only {
		position: absolute;
		left: -9999px;
		width: 1px;
		height: 1px;
		overflow: hidden;
	}
</style>
